import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { 
  ArrowLeftIcon, 
  TrophyIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface MyHorsePicksData {
  poolId: string
  poolName: string
  poolSettings: {
    track: string
    raceDate: string
    pickDeadline: string
  }
  totalRaces: number
  completedPicks: number
  picks: MyHorsePick[]
  standings?: {
    rank: number
    totalPoints: number
    totalParticipants: number
  }
}

interface MyHorsePick {
  raceNumber: number
  race: {
    postTime: string
    raceType: string
    conditions: string
    purse: string
    distance?: string
    status: 'upcoming' | 'running' | 'final'
    results?: {
      winner?: string
      place?: string
      show?: string
    }
  }
  pick?: {
    horseId: string
    horseName: string
    postPosition: string
    postPositionColor?: {
      background_color: string
      text_color: string
      name: string
    }
    jockey: string
    trainer: string
    isScratched: boolean
    pickedAt: string
  }
  result?: {
    result: 'win' | 'place' | 'show' | 'loss' | 'pending'
    points: number
    finalPosition?: number
  }
}

export default function ViewMyHorsePicksPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const { user, getAccessTokenSilently } = useAuth0()
  const [picksData, setPicksData] = useState<MyHorsePicksData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (poolId) {
      fetchMyPicks()
    }
  }, [poolId])

  const fetchMyPicks = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      // First, fetch pool details to get race information (same as Make Picks page)
      const poolResponse = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!poolResponse.ok) {
        throw new Error(`Failed to fetch pool: ${poolResponse.statusText}`)
      }

      const poolData = await poolResponse.json()
      
      // Then fetch user's picks
      const picksResponse = await fetch(`${API_BASE_URL}/pools/${poolId}/horse-picks`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!picksResponse.ok) {
        throw new Error(`Failed to fetch picks: ${picksResponse.statusText}`)
      }

      const picksData = await picksResponse.json()
      
      // Create a map of races by race number for quick lookup
      const raceMap = new Map()
      if (poolData.pool.races) {
        poolData.pool.races.forEach((race: any) => {
          raceMap.set(race.raceNumber, race)
        })
      }
      
      // Combine picks with race information (same pattern as Make Picks page)
      const enhancedPicks = picksData.picks?.map((pick: any) => {
        const race = raceMap.get(pick.raceNumber)
        return {
          raceNumber: pick.raceNumber,
          race: race ? {
            postTime: race.postTime || 'TBD',
            raceType: race.raceType || 'Unknown',
            conditions: race.conditions || 'Unknown',
            purse: race.purse || '0',
            distance: race.distance || 'Unknown',
            status: race.status || 'upcoming'
          } : {
            postTime: 'TBD',
            raceType: 'Unknown',
            conditions: 'Unknown',
            purse: '0',
            distance: 'Unknown',
            status: 'upcoming'
          },
          pick: {
            horseId: pick.horseId,
            horseName: pick.horseName,
            postPosition: pick.postPosition,
            postPositionColor: pick.postPositionColor,
            jockey: pick.jockey,
            trainer: pick.trainer,
            isScratched: false,
            pickedAt: pick.createdAt || pick.updatedAt
          }
        }
      }) || []
      
      // Create the compatible data structure
      const compatibleData = {
        poolId: poolData.pool.poolId,
        poolName: poolData.pool.name,
        poolSettings: picksData.poolSettings || {
          track: poolData.pool.track || 'Unknown',
          raceDate: poolData.pool.endDate || 'TBD',
          pickDeadline: poolData.pool.pickDeadline || 'TBD'
        },
        totalRaces: poolData.pool.races?.length || 0,
        completedPicks: picksData.completedPicks || enhancedPicks.length,
        picks: enhancedPicks,
        standings: picksData.standings
      }
      
      setPicksData(compatibleData)
    } catch (err) {
      console.error('Error fetching picks:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch picks')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getResultColor = (result: string) => {
    switch (result) {
      case 'win':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'place':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'show':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'loss':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'pending':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const getResultIcon = (result: string) => {
    switch (result) {
      case 'win':
        return '🥇'
      case 'place':
        return '🥈'
      case 'show':
        return '🥉'
      case 'loss':
        return '❌'
      case 'pending':
        return '⏳'
      default:
        return '❓'
    }
  }

  const getRaceStatusColor = (status: string) => {
    switch (status) {
      case 'final':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'running':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'upcoming':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const completionPercentage = picksData ? Math.round((picksData.completedPicks / picksData.totalRaces) * 100) : 0
  const isDeadlinePassed = picksData ? new Date() > new Date(picksData.poolSettings.pickDeadline) : false

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  if (!picksData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No picks data available</h2>
          <p className="text-gray-600 dark:text-gray-300">Your picks will appear here once you start making selections.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <Link
                to={`/pools/${poolId}`}
                className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-1" />
                Back to Pool
              </Link>
            </div>
            <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              My Picks - {picksData.poolName}
            </h1>
            <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">
              {picksData.poolSettings.track} • {formatDate(picksData.poolSettings.raceDate)}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to={`/pools/${poolId}/standings`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <TrophyIcon className="w-4 h-4 mr-2" />
              View Standings
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <CheckCircleIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Picks Completed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {picksData.completedPicks} / {picksData.totalRaces}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{completionPercentage}%</p>
            </div>
          </div>
        </div>

        {picksData.standings && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center">
                <TrophyIcon className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Current Rank</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    #{picksData.standings.rank}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    of {picksData.standings.totalParticipants}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center">
                <span className="text-2xl mr-4">🎯</span>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Points</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {picksData.standings.totalPoints}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <ClockIcon className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Pick Deadline</p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                {formatDateTime(picksData.poolSettings.pickDeadline)}
              </p>
              {isDeadlinePassed && (
                <p className="text-xs text-red-600 dark:text-red-400">Deadline passed</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Completion Status */}
      {picksData.completedPicks < picksData.totalRaces && !isDeadlinePassed && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Incomplete Picks
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                You have {picksData.totalRaces - picksData.completedPicks} races left to pick. 
                <Link 
                  to={`/pools/${poolId}/picks`}
                  className="ml-1 font-medium underline hover:no-underline"
                >
                  Complete your picks now
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Picks List */}
      <div className="space-y-6">
        {picksData.picks.map((pickData) => (
          <div
            key={pickData.raceNumber}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Race {pickData.raceNumber} - {pickData.race?.raceType || 'Unknown'}
                  </h3>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {pickData.race?.postTime || 'TBD'} • {pickData.race?.distance || 'Unknown'} • ${pickData.race?.purse ? parseInt(pickData.race.purse).toLocaleString() : 'TBD'}
                    </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRaceStatusColor(pickData.race?.status || 'upcoming')}`}>
                      {(pickData.race?.status || 'upcoming').charAt(0).toUpperCase() + (pickData.race?.status || 'upcoming').slice(1)}
                    </span>
                  </div>
                </div>
                {pickData.result && (
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{getResultIcon(pickData.result.result)}</span>
                    <div className="text-right">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getResultColor(pickData.result.result)}`}>
                        {pickData.result.result === 'pending' ? 'Pending' : 
                         pickData.result.result === 'loss' ? 'Loss' :
                         pickData.result.result.charAt(0).toUpperCase() + pickData.result.result.slice(1)}
                      </div>
                      <div className="text-sm font-bold text-gray-900 dark:text-white mt-1">
                        {pickData.result.points} points
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6">
              {pickData.pick ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {pickData.pick.postPositionColor ? (
                      <div
                        className="w-12 h-12 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center text-lg font-bold"
                        style={{
                          backgroundColor: pickData.pick.postPositionColor.background_color,
                          color: pickData.pick.postPositionColor.text_color
                        }}
                      >
                        {pickData.pick.postPosition}
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-lg font-bold">
                        {pickData.pick.postPosition}
                      </div>
                    )}
                    <div>
                      <div className={`text-lg font-semibold ${
                        pickData.pick.isScratched 
                          ? 'text-red-600 dark:text-red-400 line-through' 
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {pickData.pick.horseName}
                        {pickData.pick.isScratched && (
                          <span className="ml-2 text-sm text-red-600 dark:text-red-400 no-underline">
                            (SCRATCHED)
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        <div>Jockey: {pickData.pick.jockey}</div>
                        <div>Trainer: {pickData.pick.trainer}</div>
                      </div>
                      {pickData.pick.postPositionColor && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Colors: {pickData.pick.postPositionColor.name}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Picked on {formatDateTime(pickData.pick.pickedAt)}
                    </div>
                    {pickData.result?.finalPosition && (
                      <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        Finished: {pickData.result.finalPosition}
                        {pickData.result.finalPosition === 1 ? 'st' :
                         pickData.result.finalPosition === 2 ? 'nd' :
                         pickData.result.finalPosition === 3 ? 'rd' : 'th'}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <EyeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No pick made</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    You haven't made a pick for this race yet.
                  </p>
                  {!isDeadlinePassed && (
                    <Link
                      to={`/pools/${poolId}/picks`}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Make Pick
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Race Conditions */}
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium">Conditions:</span> {pickData.race.conditions}
              </p>
            </div>
          </div>
        ))}
      </div>

      {picksData.picks.length === 0 && (
        <div className="text-center py-12">
          <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No races available</h2>
          <p className="text-gray-600 dark:text-gray-300">Race information will appear here once the pool is set up.</p>
        </div>
      )}
    </div>
  )
}
